package dev.lingvo.site.components.editor

import androidx.compose.runtime.staticCompositionLocalOf
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.await
import kotlinx.coroutines.suspendCancellableCoroutine
import org.w3c.dom.Element
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLTextAreaElement
import kotlin.coroutines.resume

internal class EditorCapturedFocusRegistry {
    private var nextToken = 1L
    private val captured = mutableMapOf<Long, HTMLElement>()

    internal fun capture(element: HTMLElement?): Long? {
        val target = element?.takeIf(HTMLElement::isEditorConnected) ?: return null
        val token = nextToken++
        captured[token] = target
        return token
    }

    internal fun resolve(token: Long): HTMLElement? {
        val element = captured[token] ?: return null
        if (!element.isEditorConnected()) {
            captured.remove(token)
            return null
        }
        captured.remove(token)
        return element
    }

    internal fun clear() {
        captured.clear()
    }
}

internal data class EditorTargetSelection(
    val target: EditorFocusRef.Target,
    val value: String,
    val selectionStart: Int,
    val selectionEnd: Int,
)

internal data class EditorTargetSelectionRestore(
    val target: EditorFocusRef.Target,
    val caret: Int,
)

internal data class EditorWorkbenchMeasurement(
    val editorScrollClientWidth: Double,
    val editorScrollOffsetWidth: Double,
    val contentClientWidth: Double,
    val contentInnerWidth: Double,
    val borderInlineStartWidth: Double,
    val borderInlineEndWidth: Double,
    val paddingInlineStart: Double,
    val paddingInlineEnd: Double,
    val scrollbarGutter: Double,
    val activeTargetWidth: Double?,
)

internal interface EditorDomBridge {
    fun readTargetSelection(target: EditorFocusRef.Target): EditorTargetSelection?
    fun queueTargetSelectionRestore(restore: EditorTargetSelectionRestore)

    /**
     * Puts the caret in the target and brings [fragment] into view, for the QA blocker's "Show".
     *
     * **The selection is the scroll.** A blocking QA error names a fragment — a placeholder that
     * moved, a bracket that never closed — and the strip says which one. Finding it by eye in a
     * paragraph-length string is the work the strip was meant to save, so "Show" selects the run
     * itself: every browser scrolls a focused textarea to its own selection, which is the only
     * scroll a `<textarea>` offers (there are no child nodes inside one to `scrollIntoView`).
     *
     * **A fragment that is not in the draft still moves the caret.** The commonest blocking finding
     * is a *missing* placeholder — "Missing `{count}`" — whose fragment is by definition absent from
     * the text being searched. Refusing there would make "Show" a control that does nothing on the
     * error it is pressed for most; focusing the field is the complete and honest answer to "take me
     * to it" when there is no run to select.
     *
     * False means the target is not mounted at all, which is the one case where there is nothing to
     * take the reader to.
     */
    fun revealTargetFragment(target: EditorFocusRef.Target, fragment: String?): Boolean
    suspend fun copyText(text: String): Boolean
    suspend fun awaitAnimationFrame()
    fun activeElementEvidenceId(): String?
    fun clear()
}

/**
 * The bridge the composition reaches for when it is not the controller doing the reaching.
 *
 * `TranslationEditorScreen` owns a [BrowserEditorDomBridge] and hands it to the controller runtime
 * by hand; the QA blocker sits five composables deeper, in `EditorActionBar`, and threading one
 * object through `EditorScreenContent`, `EditorComposition` and `TranslationPane` to reach it would
 * put a parameter no layer in between has any use for into three signatures. The screen provides
 * its own instance over the content, so production still has exactly one bridge; the default keeps
 * isolated component fixtures mountable, the same way `LocalEditorUtilityPresence` does.
 */
private val SharedBrowserEditorDomBridge: EditorDomBridge by lazy { BrowserEditorDomBridge() }

internal val LocalEditorDomBridge = staticCompositionLocalOf<EditorDomBridge> { SharedBrowserEditorDomBridge }

private interface EditorTargetSelectionConsumer {
    fun takeTargetSelectionRestore(target: EditorFocusRef.Target): EditorTargetSelectionRestore?
}

internal class BrowserEditorDomBridge : EditorDomBridge, EditorTargetSelectionConsumer {
    private val pendingSelectionRestores = mutableMapOf<EditorFocusRef.Target, EditorTargetSelectionRestore>()

    override fun readTargetSelection(target: EditorFocusRef.Target): EditorTargetSelection? {
        val root = findActiveTargetRoot(target) ?: return null
        val textarea = root.querySelector("textarea") as? HTMLTextAreaElement ?: return null
        if (document.activeElement !== textarea) return null
        val value = textarea.value
        val maximum = value.length
        val selectionStart = (textarea.selectionStart ?: 0).coerceIn(0, maximum)
        val selectionEnd = (textarea.selectionEnd ?: selectionStart).coerceIn(0, maximum)
        return EditorTargetSelection(
            target = target,
            value = value,
            selectionStart = selectionStart,
            selectionEnd = selectionEnd,
        )
    }

    override fun queueTargetSelectionRestore(restore: EditorTargetSelectionRestore) {
        pendingSelectionRestores[restore.target] = restore
    }

    override fun revealTargetFragment(target: EditorFocusRef.Target, fragment: String?): Boolean {
        val root = findActiveTargetRoot(target) ?: return false
        val textarea = root.querySelector("textarea") as? HTMLTextAreaElement ?: return false
        val trimmed = fragment?.takeIf(String::isNotBlank)
        val start = trimmed?.let { textarea.value.indexOf(it) }?.takeIf { it >= 0 }
        textarea.focus()
        if (start != null && trimmed != null) {
            textarea.setSelectionRange(start, start + trimmed.length)
        }
        return true
    }

    override suspend fun copyText(text: String): Boolean = try {
        window.navigator.clipboard.writeText(text).await()
        true
    } catch (cancellation: CancellationException) {
        throw cancellation
    } catch (_: Throwable) {
        false
    }

    override suspend fun awaitAnimationFrame() {
        suspendCancellableCoroutine { continuation ->
            window.requestAnimationFrame {
                if (continuation.isActive) continuation.resume(Unit)
            }
        }
    }

    override fun activeElementEvidenceId(): String? =
        (document.activeElement as? HTMLElement)?.id?.takeIf(String::isNotBlank)

    override fun clear() {
        pendingSelectionRestores.clear()
    }

    override fun takeTargetSelectionRestore(target: EditorFocusRef.Target): EditorTargetSelectionRestore? =
        pendingSelectionRestores.remove(target)
}

internal class BrowserEditorViewEffectEnvironment(
    private val capturedFocus: EditorCapturedFocusRegistry,
    private val domBridge: EditorDomBridge,
) : EditorViewEffectEnvironment, EditorFocusSelectionRestorer {
    override fun resolve(ref: EditorFocusRef): EditorViewEffectNode? = when (ref) {
        is EditorFocusRef.Captured -> capturedFocus.resolve(ref.token)
        is EditorFocusRef.ElementId -> document.getElementById(ref.value) as? HTMLElement
        is EditorFocusRef.QueueRow -> document.getElementById(
            "$KEY_ROW_ID_PREFIX${ref.keyId}",
        ) as? HTMLElement

        is EditorFocusRef.Target -> findTargetTextArea(ref)
        is EditorFocusRef.TargetSnapshot -> findTargetTextArea(ref.target)
        EditorFocusRef.Search -> findSearchInput()
    }
        ?.takeIf(HTMLElement::isEditorUsable)
        ?.let(::BrowserEditorViewEffectNode)

    override fun queueScroller(): EditorViewEffectNode? =
        (document.querySelector("[data-editor-queue-scroll]") as? HTMLElement)
            ?.takeIf(HTMLElement::isEditorUsable)
            ?.let(::BrowserEditorViewEffectNode)

    override fun activeNode(): EditorViewEffectNode? =
        (document.activeElement as? HTMLElement)
            ?.takeIf(HTMLElement::isEditorConnected)
            ?.let(::BrowserEditorViewEffectNode)

    override suspend fun awaitAnimationFrame() = domBridge.awaitAnimationFrame()

    override fun consumeAndRestoreSelection(target: EditorFocusRef.Target) {
        val bridge = domBridge as? EditorTargetSelectionConsumer ?: return
        val restore = bridge.takeTargetSelectionRestore(target) ?: return
        val textarea = findTargetTextArea(target) ?: return
        if (document.activeElement !== textarea) return
        val caret = restore.caret.coerceIn(0, textarea.value.length)
        textarea.setSelectionRange(caret, caret)
    }

    override fun restoreSelection(snapshot: EditorFocusRef.TargetSnapshot) {
        val textarea = findTargetTextArea(snapshot.target) ?: return
        if (document.activeElement !== textarea) return
        val maximum = textarea.value.length
        val start = snapshot.selectionStart.coerceIn(0, maximum)
        val end = snapshot.selectionEnd.coerceIn(start, maximum)
        textarea.asDynamic().setSelectionRange(start, end, snapshot.selectionDirection)
    }
}

private class BrowserEditorViewEffectNode(
    private val element: HTMLElement,
) : EditorViewEffectNode {
    override val connected: Boolean
        get() = element.isEditorConnected()

    override val clientHeight: Double
        get() = element.clientHeight.toDouble()

    override val scrollHeight: Double
        get() = element.scrollHeight.toDouble()

    override var scrollTop: Double
        get() = element.scrollTop
        set(value) {
            element.scrollTop = value
        }

    override fun focus() {
        element.focus()
    }

    override fun equals(other: Any?): Boolean =
        other is BrowserEditorViewEffectNode && other.element === element

    override fun hashCode(): Int = element.hashCode()
}

internal fun readEditorWorkbenchMeasurement(): EditorWorkbenchMeasurement? {
    val editorScroll = document.querySelector("[data-editor-scroll]") as? HTMLElement ?: return null
    val content = document.querySelector("[data-editor-content]") as? HTMLElement ?: return null
    if (!editorScroll.isEditorConnected() || !content.isEditorConnected()) return null

    val scrollStyle = window.getComputedStyle(editorScroll)
    val contentStyle = window.getComputedStyle(content)
    val borderInlineStartWidth = cssPixels(
        scrollStyle.getPropertyValue("border-inline-start-width"),
    )
    val borderInlineEndWidth = cssPixels(
        scrollStyle.getPropertyValue("border-inline-end-width"),
    )
    val paddingInlineStart = cssPixels(
        contentStyle.getPropertyValue("padding-inline-start"),
    )
    val paddingInlineEnd = cssPixels(
        contentStyle.getPropertyValue("padding-inline-end"),
    )
    val scrollbarGutter = (
        editorScroll.offsetWidth - editorScroll.clientWidth -
            borderInlineStartWidth - borderInlineEndWidth
        ).coerceAtLeast(0.0)
    val activeTargetWidth = findMeasuredTargetRoot(content)
        ?.getBoundingClientRect()
        ?.width

    return EditorWorkbenchMeasurement(
        editorScrollClientWidth = editorScroll.clientWidth.toDouble(),
        editorScrollOffsetWidth = editorScroll.offsetWidth.toDouble(),
        contentClientWidth = content.clientWidth.toDouble(),
        contentInnerWidth = content.clientWidth - paddingInlineStart - paddingInlineEnd,
        borderInlineStartWidth = borderInlineStartWidth,
        borderInlineEndWidth = borderInlineEndWidth,
        paddingInlineStart = paddingInlineStart,
        paddingInlineEnd = paddingInlineEnd,
        scrollbarGutter = scrollbarGutter,
        activeTargetWidth = activeTargetWidth,
    )
}

private fun findSearchInput(): HTMLElement? {
    val searchRoot = document.getElementById(EDITOR_SEARCH_ID) ?: return null
    return (searchRoot.querySelector("input") as? HTMLElement)
        ?.takeIf(HTMLElement::isEditorUsable)
}

private fun findTargetTextArea(target: EditorFocusRef.Target): HTMLTextAreaElement? =
    findActiveTargetRoot(target)?.querySelector("textarea") as? HTMLTextAreaElement

private fun findActiveTargetRoot(target: EditorFocusRef.Target): HTMLElement? {
    val expectedCategory = target.pluralCategory?.name.orEmpty()
    val nodes = document.querySelectorAll("[data-editor-target]")
    for (index in 0 until nodes.length) {
        val root = nodes.item(index) as? HTMLElement ?: continue
        if (!root.isEditorUsable()) continue
        if (root.getAttribute("data-editor-key-id") != target.keyId) continue
        if (root.getAttribute("data-editor-plural-category").orEmpty() != expectedCategory) continue
        if (
            target.locale != null &&
            root.getAttribute("data-editor-locale") != target.locale
        ) continue
        val textarea = root.querySelector("textarea") as? HTMLTextAreaElement ?: continue
        if (textarea.isEditorUsable()) return root
    }
    return null
}

private fun findMeasuredTargetRoot(content: HTMLElement): HTMLElement? =
    (content.querySelector("[data-editor-target]") as? HTMLElement)
        ?.takeIf(HTMLElement::isEditorConnected)

private fun cssPixels(value: String): Double = value.trim().removeSuffix("px").toDoubleOrNull() ?: 0.0

internal fun HTMLElement.isEditorConnected(): Boolean = asDynamic().isConnected == true

/**
 * Whether an element is really there to be focused, measured or scrolled.
 *
 * **One definition, shared with `EditorControllerDom`.** There were two, byte for byte identical,
 * in the two files the focus system is split across — and the same for the connectedness test and
 * for "is this a text entry" (that one lives in `EditorShortcuts`). Four copies of two rules that
 * must agree, in the code where disagreeing means focus lands somewhere the reader cannot see.
 */
internal fun HTMLElement.isEditorUsable(): Boolean {
    if (!isEditorConnected()) return false
    var current: Element? = this
    while (current != null) {
        if (
            current.getAttribute("aria-hidden") == "true" ||
            current.getAttribute("hidden") != null ||
            current.getAttribute("inert") != null
        ) {
            return false
        }
        val html = current as? HTMLElement
        if (html != null) {
            val style = window.getComputedStyle(html)
            if (style.display == "none" || style.visibility == "hidden") return false
        }
        current = current.parentElement
    }
    return true
}
